import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Loader2, Heart, Activity, Pill, ClipboardList, FileText, Clock,
  Plus, CheckCircle, AlertTriangle, TrendingUp, Stethoscope, Save, User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function PatientSelector({ patients, selected, onSelect }) {
  return (
    <select value={selected} onChange={e => onSelect(e.target.value)}
      className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm bg-sand-50 mb-4"
      data-testid="chart-patient-select">
      <option value="">Select patient...</option>
      {patients.map(p => (
        <option key={p.id} value={p.id}>{p.name} — {p.primary_diagnosis || 'N/A'} (PPS: {p.pps_score || 'N/A'})</option>
      ))}
    </select>
  );
}

// ═══════════════════════════════════════════════════════
// VITALS TAB
// ═══════════════════════════════════════════════════════
function VitalsPanel({ patientId }) {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ pain_level: '', heart_rate: '', blood_pressure_systolic: '', blood_pressure_diastolic: '', respiratory_rate: '', oxygen_saturation: '', temperature: '', weight: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    fetch(`${API}/api/chart/vitals/${patientId}`)
      .then(r => r.json()).then(d => setVitals(d.vitals || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  const saveVitals = async () => {
    setSaving(true);
    try {
      const body = { patient_id: patientId, recorded_by: "Staff" };
      Object.entries(form).forEach(([k, v]) => { if (v !== '') body[k] = Number(v); });
      const res = await fetch(`${API}/api/chart/vitals`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const record = await res.json();
      setVitals(prev => [record, ...prev]);
      setForm({ pain_level: '', heart_rate: '', blood_pressure_systolic: '', blood_pressure_diastolic: '', respiratory_rate: '', oxygen_saturation: '', temperature: '', weight: '' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div data-testid="vitals-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-sage-400" />
          <h3 className="text-base font-heading font-semibold text-[#2C3D30]">Vital Signs</h3>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="rounded-full bg-sage-400 hover:bg-sage-500 text-xs" data-testid="vitals-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Record Vitals
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 rounded-2xl bg-sage-50 border border-sage-100 space-y-2" data-testid="vitals-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { key: 'pain_level', label: 'Pain (0-10)', max: 10 },
              { key: 'heart_rate', label: 'HR (bpm)' },
              { key: 'blood_pressure_systolic', label: 'BP Sys' },
              { key: 'blood_pressure_diastolic', label: 'BP Dia' },
              { key: 'respiratory_rate', label: 'RR' },
              { key: 'oxygen_saturation', label: 'O2 Sat %' },
              { key: 'temperature', label: 'Temp (F)' },
              { key: 'weight', label: 'Weight (lbs)' },
            ].map(f => (
              <Input key={f.key} type="number" value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.label} className="text-sm rounded-xl border-sand-200" data-testid={`vitals-${f.key}`} />
            ))}
          </div>
          <Button onClick={saveVitals} disabled={saving} className="rounded-full bg-sage-400 hover:bg-sage-500 text-xs" data-testid="vitals-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-sage-400 mx-auto" /> : (
        <div className="space-y-2" data-testid="vitals-list">
          {vitals.length === 0 ? <p className="text-xs text-[#5C6A5F] text-center py-6">No vitals recorded yet.</p> :
            vitals.map(v => (
              <div key={v.id} className="p-3 rounded-xl bg-sand-50 border border-sand-200 flex flex-wrap gap-3 items-center text-xs">
                {v.pain_level != null && <Badge className={`rounded-full ${v.pain_level >= 7 ? 'bg-terra-400 text-white' : v.pain_level >= 4 ? 'bg-terra-100 text-terra-500' : 'bg-sage-50 text-sage-400'}`}>Pain: {v.pain_level}/10</Badge>}
                {v.heart_rate && <span className="text-[#5C6A5F]">HR: {v.heart_rate}</span>}
                {v.bp_systolic && <span className="text-[#5C6A5F]">BP: {v.bp_systolic}/{v.bp_diastolic}</span>}
                {v.respiratory_rate && <span className="text-[#5C6A5F]">RR: {v.respiratory_rate}</span>}
                {v.o2_sat && <span className="text-[#5C6A5F]">O2: {v.o2_sat}%</span>}
                {v.temperature && <span className="text-[#5C6A5F]">T: {v.temperature}F</span>}
                <span className="ml-auto text-sand-400">{new Date(v.recorded_at).toLocaleString()}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAR TAB
// ═══════════════════════════════════════════════════════
function MARPanel({ patientId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ medication_name: '', dose: '', route: 'PO', pain_before: '', pain_after: '', effectiveness: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    fetch(`${API}/api/chart/mar/${patientId}`)
      .then(r => r.json()).then(d => setRecords(d.records || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  const saveMAR = async () => {
    if (!form.medication_name) return;
    setSaving(true);
    try {
      const body = { patient_id: patientId, ...form, administered_by: "Staff" };
      if (body.pain_before) body.pain_before = Number(body.pain_before);
      if (body.pain_after) body.pain_after = Number(body.pain_after);
      const res = await fetch(`${API}/api/chart/mar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const record = await res.json();
      setRecords(prev => [record, ...prev]);
      setForm({ medication_name: '', dose: '', route: 'PO', pain_before: '', pain_after: '', effectiveness: '', notes: '' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div data-testid="mar-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Pill className="w-5 h-5 text-sky-400" />
          <h3 className="text-base font-heading font-semibold text-[#2C3D30]">Medication Administration</h3>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="rounded-full bg-sky-300 hover:bg-sky-400 text-white text-xs" data-testid="mar-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Record Admin
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 rounded-2xl bg-sky-50 border border-sky-100 space-y-2" data-testid="mar-form">
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.medication_name} onChange={e => setForm(p => ({ ...p, medication_name: e.target.value }))} placeholder="Medication" className="rounded-xl" data-testid="mar-med-name" />
            <Input value={form.dose} onChange={e => setForm(p => ({ ...p, dose: e.target.value }))} placeholder="Dose (e.g., 15mg)" className="rounded-xl" data-testid="mar-dose" />
            <Input type="number" value={form.pain_before} onChange={e => setForm(p => ({ ...p, pain_before: e.target.value }))} placeholder="Pain Before" className="rounded-xl" data-testid="mar-pain-before" />
            <Input type="number" value={form.pain_after} onChange={e => setForm(p => ({ ...p, pain_after: e.target.value }))} placeholder="Pain After" className="rounded-xl" data-testid="mar-pain-after" />
          </div>
          <Button onClick={saveMAR} disabled={saving} className="rounded-full bg-sky-300 hover:bg-sky-400 text-white text-xs" data-testid="mar-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-sky-300 mx-auto" /> : (
        <div className="space-y-2" data-testid="mar-list">
          {records.length === 0 ? <p className="text-xs text-[#5C6A5F] text-center py-6">No medication records yet.</p> :
            records.map(r => (
              <div key={r.id} className="p-3 rounded-xl bg-sand-50 border border-sand-200 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-[#2C3D30]">{r.medication_name} {r.dose}</span>
                  <span className="text-sand-400">{new Date(r.administered_at).toLocaleString()}</span>
                </div>
                <div className="flex gap-3 text-[#5C6A5F]">
                  <span>Route: {r.route}</span>
                  {r.pain_before != null && <span>Pain: {r.pain_before} → {r.pain_after ?? '?'}</span>}
                  {r.effectiveness && <span>Effect: {r.effectiveness}</span>}
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// ORDERS TAB
// ═══════════════════════════════════════════════════════
function OrdersPanel({ patientId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ order_type: 'medication', description: '', urgency: 'routine' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    fetch(`${API}/api/chart/orders/${patientId}`)
      .then(r => r.json()).then(d => setOrders(d.orders || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  const saveOrder = async () => {
    if (!form.description) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/chart/order`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ patient_id: patientId, ...form }) });
      const record = await res.json();
      setOrders(prev => [record, ...prev]);
      setForm({ order_type: 'medication', description: '', urgency: 'routine' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  const urgencyColors = { stat: 'bg-terra-400 text-white', urgent: 'bg-terra-100 text-terra-500', routine: 'bg-sand-200 text-[#5C6A5F]' };
  const statusColors = { pending: 'bg-sky-50 text-sky-500', active: 'bg-sage-50 text-sage-400', completed: 'bg-sand-200 text-[#5C6A5F]', discontinued: 'bg-terra-50 text-terra-400' };

  return (
    <div data-testid="orders-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-terra-400" />
          <h3 className="text-base font-heading font-semibold text-[#2C3D30]">Physician Orders</h3>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="rounded-full bg-terra-400 hover:bg-terra-500 text-white text-xs" data-testid="order-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Order
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 rounded-2xl bg-terra-50 border border-terra-100 space-y-2" data-testid="order-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.order_type} onChange={e => setForm(p => ({ ...p, order_type: e.target.value }))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white" data-testid="order-type">
              <option value="medication">Medication</option>
              <option value="treatment">Treatment</option>
              <option value="diagnostic">Diagnostic</option>
              <option value="referral">Referral</option>
              <option value="diet">Diet</option>
            </select>
            <select value={form.urgency} onChange={e => setForm(p => ({ ...p, urgency: e.target.value }))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white" data-testid="order-urgency">
              <option value="routine">Routine</option>
              <option value="urgent">Urgent</option>
              <option value="stat">STAT</option>
            </select>
          </div>
          <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Order description..." className="rounded-xl" data-testid="order-description" />
          <Button onClick={saveOrder} disabled={saving} className="rounded-full bg-terra-400 hover:bg-terra-500 text-white text-xs" data-testid="order-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save Order
          </Button>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-terra-400 mx-auto" /> : (
        <div className="space-y-2" data-testid="orders-list">
          {orders.length === 0 ? <p className="text-xs text-[#5C6A5F] text-center py-6">No orders yet.</p> :
            orders.map(o => (
              <div key={o.id} className="p-3 rounded-xl bg-sand-50 border border-sand-200 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-full text-xs ${urgencyColors[o.urgency]}`}>{o.urgency}</Badge>
                    <Badge variant="outline" className="rounded-full text-xs">{o.order_type}</Badge>
                    <Badge className={`rounded-full text-xs ${statusColors[o.status]}`}>{o.status}</Badge>
                  </div>
                  <span className="text-sand-400">{new Date(o.created_at).toLocaleString()}</span>
                </div>
                <p className="text-[#2C3D30] mt-1">{o.description}</p>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function PatientChartPage() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [activeTab, setActiveTab] = useState('vitals');

  useEffect(() => {
    fetch(`${API}/api/chart/patients-list`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => setPatients(d.patients || []))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'vitals', label: 'Vitals', icon: Activity },
    { id: 'mar', label: 'MAR', icon: Pill },
    { id: 'orders', label: 'Orders', icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-sand-100" data-testid="patient-chart-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">Patient Chart</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Clinical data entry, vitals tracking, and order management</p>
        </div>

        <PatientSelector patients={patients} selected={selectedPatient} onSelect={setSelectedPatient} />

        {selectedPatient && (
          <>
            <div className="flex gap-2 mb-6 pb-3 border-b border-sand-200">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeTab === tab.id ? 'bg-sage-50 text-sage-400 border border-sage-200' : 'text-[#5C6A5F] hover:bg-sand-200'
                  }`} data-testid={`chart-tab-${tab.id}`}>
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="bg-sand-50 rounded-2xl border border-sand-200 p-4 sm:p-6">
              {activeTab === 'vitals' && <VitalsPanel patientId={selectedPatient} />}
              {activeTab === 'mar' && <MARPanel patientId={selectedPatient} />}
              {activeTab === 'orders' && <OrdersPanel patientId={selectedPatient} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
